// src/services/socketService.js
import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.connecting = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
  }

  initialize(token) {
    // Prevent multiple initialization attempts
    if (this.connecting) {
      // console.log('[Socket] Already connecting, skipping...');
      return this.socket;
    }

    if (this.socket && this.socket.connected) {
      // console.log('[Socket] Already connected');
      return this.socket;
    }

    try {
      this.connecting = true;
      
      // Disconnect existing connection properly
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }
      

        const API_URL2 = import.meta.env.VITE_API_URL2;


      this.socket = io(API_URL2, {
        auth: {
          token: token
        },
        // Start with polling to avoid transport errors
        transports: ['polling'],
        upgrade: true, // Allow upgrade to websocket after connection
        rememberUpgrade: false, // Don't remember the upgrade
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 3000,
        reconnectionDelayMax: 10000,
        reconnectionAttempts: this.maxReconnectAttempts,
        forceNew: true,
        // Additional options to prevent transport errors
        autoConnect: true,
        query: {
          brandCode: localStorage.getItem('brandCode') || 'DEFAULT'
        }
      });

      this.setupEventListeners();
      return this.socket;
      
    } catch (error) {
      console.error('[Socket] Initialization error:', error);
      this.connecting = false;
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.socket) return;

    // Connection successful
    this.socket.on('connect', () => {
      // console.log('✅ [Socket] Connected successfully with ID:', this.socket.id);
      // console.log('✅ [Socket] Transport:', this.socket.io.engine.transport.name);
      
      this.connected = true;
      this.connecting = false;
      this.reconnectAttempts = 0;

      // Listen for transport upgrades
      this.socket.io.engine.on('upgrade', () => {
        // console.log('🔄 [Socket] Transport upgraded to:', this.socket.io.engine.transport.name);
      });
    });

    // Connection confirmed by server
    this.socket.on('connectionConfirmed', (data) => {
      // console.log('✅ [Socket] Connection confirmed by server:', data);
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      console.error('❌ [Socket] Connection error:', error.message);
      this.connected = false;
      this.connecting = false;
      
      // Handle specific error types
      if (error.message?.includes('Authentication') || 
          error.message?.includes('token') || 
          error.message?.includes('expired') ||
          error.message?.includes('401')) {
        // console.log('[Socket] Auth error - clearing token');
        localStorage.removeItem('token');
        localStorage.removeItem('brandToken');
        this.socket?.disconnect();
        return;
      }

      // Handle transport errors
      if (error.message?.includes('transport') || error.type === 'TransportError') {
        // console.log('[Socket] Transport error - will retry with polling only');
        this.reconnectAttempts++;
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          setTimeout(() => {
            this.forceReconnect(localStorage.getItem('brandToken') || localStorage.getItem('token'));
          }, 5000);
        }
      }
    });

    // Disconnection
    this.socket.on('disconnect', (reason) => {
      // console.log('⚠️ [Socket] Disconnected:', reason);
      this.connected = false;
      this.connecting = false;
      
      // Handle transport errors on disconnect
      if (reason === 'transport error' || reason === 'transport close') {
        // console.log('[Socket] Transport error on disconnect');
        this.reconnectAttempts++;
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          // console.log(`[Socket] Will retry connection (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => {
            const token = localStorage.getItem('brandToken') || localStorage.getItem('token');
            if (token) {
              this.forceReconnect(token);
            }
          }, 5000);
        } else {
          console.log('[Socket] Max reconnection attempts reached');
        }
        return;
      }
      
      // Don't auto-reconnect on intentional disconnect
      if (reason === 'io client disconnect' || reason === 'io server disconnect') {
        // console.log('[Socket] Intentional disconnect - not reconnecting');
        return;
      }
    });

    // Reconnection attempt
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      // console.log(`🔄 [Socket] Reconnection attempt ${attemptNumber}/${this.maxReconnectAttempts}`);
    });

    // Reconnection successful
    this.socket.on('reconnect', () => {
      // console.log('✅ [Socket] Reconnected successfully');
      this.connected = true;
      this.connecting = false;
      this.reconnectAttempts = 0;
    });

    // Reconnection failed
    this.socket.on('reconnect_failed', () => {
      // console.error('❌ [Socket] Reconnection failed after max attempts');
      this.connected = false;
      this.connecting = false;
    });

    // Handle ping/pong
    this.socket.on('ping', () => {
      this.socket.emit('pong');
    });
  }

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  emit(event, data) {
    if (this.socket && this.connected) {
      this.socket.emit(event, data);
      return true;
    } else {
      console.warn('[Socket] Cannot emit - not connected');
      return false;
    }
  }

  markAsRead(phoneNumber) {
    return this.emit('markAsRead', { 
      phoneNumber,
      brandCode: localStorage.getItem('brandCode') || 'DEFAULT'
    });
  }

  disconnect() {
    if (this.socket) {
      // console.log('[Socket] Disconnecting...');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.connecting = false;
      this.reconnectAttempts = 0;
    }
  }

  isConnected() {
    return this.connected && this.socket?.connected;
  }

  // Force reconnect method
  forceReconnect(token) {
    // console.log('[Socket] Force reconnecting...');
    this.disconnect();
    
    setTimeout(() => {
      if (token) {
        this.initialize(token);
      }
    }, 2000);
  }
}

export default new SocketService();
